import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';

export const ImageLightbox = ({ images, initialIndex = 0, isOpen, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
	const selectedRef = useRef(null);
	const touchStartX = useRef(0);
	const touchEndX = useRef(0);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex]);

	useEffect(() => {
		if (selectedRef.current) {
			selectedRef.current.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
				inline: "center", // centers it horizontally in the slider
			});
		}
	}, [currentIndex]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

	const handleTouchStart = (e) => {
		touchStartX.current = e.touches[0].clientX;
	};

	const handleTouchMove = (e) => {
		touchEndX.current = e.touches[0].clientX;
	};

	const handleTouchEnd = () => {
		const swipeDistance = touchStartX.current - touchEndX.current;
		const minSwipeDistance = 50; // px threshold to count as a swipe

		if (swipeDistance > minSwipeDistance) {
			// Swiped left → next image
			nextImage();
		} else if (swipeDistance < -minSwipeDistance) {
			// Swiped right → previous image
			prevImage();
		}
	};

  if (!isOpen || !images || images.length === 0) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-center justify-center"
          data-testid="image-lightbox"
        >
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/95 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-3 text-white/70 hover:text-white transition-colors"
            data-testid="lightbox-close-btn"
          >
            <X className="w-8 h-8" strokeWidth={1.5} />
          </button>

          {/* Image counter */}
          <div className="absolute top-4 left-4 z-10 text-white/70 text-sm">
            {currentIndex + 1} / {images.length}
          </div>

          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-4 z-10 p-3 text-white/70 hover:text-white transition-colors"
                data-testid="lightbox-prev-btn"
              >
                <ChevronLeft className="w-10 h-10" strokeWidth={1.5} />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 z-10 p-3 text-white/70 hover:text-white transition-colors"
                data-testid="lightbox-next-btn"
              >
                <ChevronRight className="w-10 h-10" strokeWidth={1.5} />
              </button>
            </>
          )}

          {/* Main image */}
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="relative z-10 max-w-[90vw] max-h-[90vh] flex items-center justify-center"
						onTouchStart={ handleTouchStart }
						onTouchMove={ handleTouchMove }
						onTouchEnd={ handleTouchEnd }
          >
            <img
              src={ images[currentIndex].preview }
              alt={`Image ${currentIndex + 1}`}
              className="max-w-full max-h-[90vh] object-contain rounded-sm"
              data-testid="lightbox-image"
            />
          </motion.div>

          {/* Thumbnail strip */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex gap-2 p-2 bg-black/50 rounded-sm max-w-[90vw] overflow-x-auto">
              {images.map((img, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`flex-shrink-0 w-16 h-16 rounded-sm overflow-hidden border-2 transition-all ${
                    index === currentIndex 
                      ? 'border-amber-700 opacity-100' 
                      : 'border-transparent opacity-50 hover:opacity-80'
                  }`}
                  data-testid={`lightbox-thumb-${index}`}
                >
                  <img
										ref={currentIndex === index ? selectedRef : null}
                    src={ img.thumbnail || img.preview }
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Magnifier button component to trigger lightbox
export const MagnifyButton = ({ onClick, className = "" }) => {
  return (
    <button
      onClick={onClick}
      className={`absolute top-4 right-4 p-2 bg-white/80 hover:bg-white rounded-full transition-colors ${className}`}
      title="View larger"
      data-testid="magnify-btn"
    >
      <ZoomIn className="w-5 h-5 text-stone-800" strokeWidth={1.5} />
    </button>
  );
};
